import { useMemo, useState } from 'react'
import { Clock } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SegmentedToggle } from '@/components/sales-report/components/SegmentedToggle'
import type { Entity, EntityPL, ProfitLossProps } from '../types'
import { ConsolidatedIncome } from './ConsolidatedIncome'
import { ConsolidatedSummary } from './ConsolidatedSummary'
import { MONO, fmt, fmtPct, symbol } from './format'
import { PLTable } from './PLTable'

const CONSOLIDATED = 'Consolidated'

/** 'Jan-26' -> 'Jan 2026'. */
function longMonth(key: string): string {
  const [mon, yy] = key.split('-')
  return `${mon} 20${yy}`
}

/** SONAR Profit & Loss — an entity toggle (Spain / US / Consolidated). Consolidated
 *  is the default: a net-profit summary (US converted to € at each month's locked
 *  rate + Spain), scoped to the fiscal YTD window where both entities have data.
 *  Spain (EUR) shows its own 2026 statement; US (USD) keeps its full 18-month
 *  history. All data via props; entity + expand state is internal. */
export function ProfitLoss({ report }: ProfitLossProps) {
  const entityByLabel: Record<string, Entity> = {}
  report.entities.forEach((e) => {
    entityByLabel[report.entityMeta[e].label] = e
  })
  const options = [...report.entities.map((e) => report.entityMeta[e].label), CONSOLIDATED]

  // Default to the Consolidated net-profit summary.
  const [view, setView] = useState<string>(CONSOLIDATED)
  const isConsolidated = view === CONSOLIDATED
  const entity = isConsolidated ? null : entityByLabel[view]

  // First index of the fiscal YTD window (first month of ytdYear, e.g. Jan-26).
  const ytdStart = useMemo(() => {
    const suffix = `-${String(report.ytdYear).slice(2)}`
    const i = report.months.findIndex((m) => m.endsWith(suffix))
    return i < 0 ? 0 : i
  }, [report.months, report.ytdYear])

  // Spain only carries YTD data, so its lens (and Consolidated) scope to that window.
  // US keeps its full 18-month history.
  const scopeToYtd = (pl: EntityPL): EntityPL => {
    const sl = (a: number[]) => a.slice(ytdStart)
    const t = pl.totals
    return {
      sections: pl.sections.map((s) => ({
        ...s,
        total: sl(s.total),
        accounts: s.accounts.map((a) => ({ ...a, values: sl(a.values) })),
      })),
      unitSubtotals: pl.unitSubtotals?.map((u) => ({ ...u, values: sl(u.values) })),
      departmentSubtotals: pl.departmentSubtotals?.map((d) => ({ ...d, values: sl(d.values) })),
      totals: {
        ...t,
        revenue: sl(t.revenue),
        cogs: sl(t.cogs),
        grossProfit: sl(t.grossProfit),
        grossMarginPct: sl(t.grossMarginPct),
        opex: sl(t.opex),
        operatingProfit: sl(t.operatingProfit),
        otherExpense: sl(t.otherExpense),
        netProfit: sl(t.netProfit),
        netMarginPct: sl(t.netMarginPct),
        fullPeriod: undefined,
      },
    }
  }

  const isUS = entity === 'us'
  const cur: EntityPL | null = isConsolidated ? null : isUS ? report.data.us : scopeToYtd(report.data[entity as Entity])
  const viewMonths = isUS ? report.months : report.months.slice(ytdStart)
  const meta = entity ? report.entityMeta[entity] : null
  const displayCurrency = isConsolidated ? 'EUR' : (meta?.currency ?? 'EUR')

  const isEmpty = !isConsolidated && !!cur && cur.sections.every((s) => s.accounts.length === 0)

  const periodLabel = viewMonths.length
    ? `${longMonth(viewMonths[0])} – ${longMonth(viewMonths[viewMonths.length - 1])}`
    : ''
  const viewCurrencyLabel = isConsolidated ? 'Consolidated €' : `${meta?.label ?? view} ${symbol(displayCurrency)}`

  const t = cur?.totals
  const marginYtd = (num: number) => (t && t.revenueYtd ? (num / t.revenueYtd) * 100 : 0)

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Profit &amp; Loss</h1>
          <p className="mt-1 text-sm text-stone-500 dark:text-stone-400">
            <span className="font-medium text-stone-700 dark:text-stone-200">
              {isConsolidated ? `${longMonth(report.months[ytdStart])} – ${longMonth(report.months[report.months.length - 1])}` : periodLabel}
            </span>{' '}
            · <span className="font-medium">{viewCurrencyLabel}</span> · YTD {report.ytdYear}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SegmentedToggle options={options} value={view} onChange={setView} ariaLabel="Entity" />
        </div>
      </div>

      {isConsolidated ? (
        <>
          <ConsolidatedSummary report={report} ytdStart={ytdStart} />
          <ConsolidatedIncome report={report} ytdStart={ytdStart} />
        </>
      ) : isEmpty ? (
        /* Defensive: an entity with no data yet */
        <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-stone-300 bg-stone-50/60 px-6 py-16 text-center dark:border-stone-700 dark:bg-stone-900/40">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-champagne-100 text-champagne-600 dark:bg-champagne-500/20 dark:text-champagne-300">
            <Clock className="h-5 w-5" />
          </span>
          <div>
            <h3 className="text-base font-semibold text-stone-800 dark:text-stone-100">{meta?.label ?? view} data pending</h3>
            <p className="mx-auto mt-1 max-w-md text-sm text-stone-500 dark:text-stone-400">
              Awaiting the monthly P&amp;L sheets for{' '}
              <span className="font-medium text-stone-600 dark:text-stone-300">{meta?.legalName ?? ''}</span>.
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* YTD KPI strip */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatCard label="Revenue" value={t!.revenueYtd} currency={displayCurrency} sub={`YTD ${report.ytdYear}`} />
            <StatCard label="Gross profit" value={t!.grossProfitYtd} currency={displayCurrency} sub={`${fmtPct(marginYtd(t!.grossProfitYtd))} margin`} signed />
            <StatCard label="Operating profit" value={t!.operatingProfitYtd} currency={displayCurrency} sub={`${fmtPct(marginYtd(t!.operatingProfitYtd))} margin`} signed />
            <StatCard label="Net profit" value={t!.netProfitYtd} currency={displayCurrency} sub={`${fmtPct(marginYtd(t!.netProfitYtd))} margin`} signed emerald />
          </div>

          {/* PC credit memo — informational, never deducted from this entity */}
          {meta?.pcCreditMemoYtd ? (
            <div className="rounded-xl border border-stone-200 bg-stone-50/70 px-4 py-3 text-sm text-stone-600 dark:border-stone-800 dark:bg-stone-800/40 dark:text-stone-300">
              <span className="font-medium text-stone-800 dark:text-stone-100">
                Memo — of which delivered against pre-paid PC credit: {fmt(meta.pcCreditMemoYtd, displayCurrency)}
              </span>{' '}
              ({fmtPct(marginYtd(meta.pcCreditMemoYtd))} of revenue). Already recognised as retainer revenue when the
              retainer was sold, so it is <strong>not</strong> deducted here — {meta.label} is shown on its own
              statutory result. It is eliminated once in the Consolidated view.
            </div>
          ) : null}

          {/* Statement */}
          <PLTable
            sections={cur!.sections}
            totals={cur!.totals}
            unitSubtotals={cur!.unitSubtotals}
            departmentSubtotals={cur!.departmentSubtotals}
            months={viewMonths}
            currency={displayCurrency}
            ytdYear={report.ytdYear}
          />
        </>
      )}
    </div>
  )
}

interface StatCardProps {
  label: string
  value: number
  currency: string
  sub?: string
  signed?: boolean
  emerald?: boolean
}

function StatCard({ label, value, currency, sub, signed, emerald }: StatCardProps) {
  const tone = signed
    ? value < 0
      ? 'text-red-600 dark:text-red-400'
      : emerald
        ? 'text-emerald-700 dark:text-emerald-400'
        : 'text-stone-900 dark:text-stone-100'
    : 'text-stone-900 dark:text-stone-100'
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-4 dark:border-stone-800 dark:bg-stone-900">
      <p className="text-xs font-medium text-stone-500 dark:text-stone-400">{label}</p>
      <p className={cn('mt-1 text-xl font-semibold', MONO, tone)}>{fmt(value, currency)}</p>
      {sub && <p className="mt-0.5 text-[11px] text-stone-400 dark:text-stone-500">{sub}</p>}
    </div>
  )
}
